/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useRef } from 'react';
import { ResponsiveLine } from '@nivo/line'; // https://nivo.rocks/line/
// material
import { themeV2 } from '@insticator/insticator-ui';
// utils
import {CHART_SETTINGS, formatYAxis, processDataForAxis} from '../../utils/charts';
// styles
import injectSheet from 'react-jss';
import styles from './linechart_styles';
import LoadingState from 'material/states/loading/loading';



/* ========== ~~~~~~~~~~ LINE CHART ~~~~~~~~~~ ========== */
const LineChart = injectSheet(styles)((props) => {

    const maxYs = [];
    let maxY = props.data.map(dataset => {
        const max = Math.max(...dataset.data.map(chartData => chartData.y), 0)
        maxYs.push(max);
        return max
    });

    maxY = props.isPercentageData ? 100 : Math.max(...maxYs.map(maxY => maxY), 0) * 1.75;

    const [currentPoint, setCurrentPoint] = useState({x: 0, y: 0, date: ''});
    const [locked, lock] = useState(false);

    const lineChartRef = useRef(null);

    useEffect(() => {
        if(props.reset && locked) {
            lock(false);
            hideTimeframeOutline();
            // setCurrentPoint({x: 0, y: 0, data: 0});
        }
    }, [props.reset]);


    useEffect(() => {
        if(locked) {
            document.addEventListener('mousedown', handleOutsideClick, false);
        } 
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick, false);
        }
    }, [locked]);

    const handleOutsideClick = e  => {
        // ignore clicks on the chart toggles
        if(e.target.classList.value.includes('charttoggle')) return null;
        lock(false);
        hideTimeframeOutline();
        setCurrentPoint({x: 0, y: 0, date: ''});
        props.onMouseLeave();
    }
    
    const hideTimeframeOutline = () => {
        for (const a of lineChartRef.current.querySelectorAll("text")) {
            a.style.fontWeight = 'normal';
            a.style.outline = 'none';
        }
    }

    const handleHover = data => {
        if(!locked) {
            const point = data.point;
            if(point.x != currentPoint.x || point.data.origDate != currentPoint.date) {
                setCurrentPoint({x: point.x, y: data.y, date: point.data.origDate});
                props.onHover && props.onHover(point);
            }
        }
        return null;
    }

    const handleClick = e => {
        setCurrentPoint({x: e.x, y:e.y, data: e.data});
        lock(true);
        for (const a of lineChartRef.current.querySelectorAll("text")) {
            if (a.textContent.includes(e.data.x)) {
                a.style.fontWeight = 'bolder';
                // a.style.outline = '1px solid rgb(192, 201, 220)';
            }
        }
        props.onHover && props.onHover(e.data);
    }

    const handleMouseLeave = e => {
        if(!locked && props.onMouseLeave) {
            setCurrentPoint({x: 0, y: 0, data: 0});
            props.onMouseLeave();
        }
    }

    const getDataLength = () => {
        let length = 0;
        props.data.forEach(item => {
            if(item.data.length > 0) length = item.data.length;
        })
        return length;
    }

    const dateLength = getDataLength();

    const lineProps = dateLength && props.period.toLowerCase() === 'daily' && !props.category === 'product' ?
        CHART_SETTINGS.linePropsForTimeScale(dateLength) :
        CHART_SETTINGS.linePropsDefualt(dateLength)

    let layers = [
        'grid', 'markers', 'axes', 'areas', 'slices', 'points',
        'mesh', 'legends', 'lines',
        d => (<CustomLine d={d} currentX={currentPoint.x} />)
    ]

    if (props.useDashedLines) {
        layers = layers.filter(layer => layer !== 'lines');
        layers.push(DashedSolidLine);
    }
    
    if(props.loading) return <div style={{ height: 400,  width: '100%', position: 'relative'}}><LoadingState /> </div>

    return (
        <div ref={lineChartRef} className={`linechart ${props.classes.linechart}`} onMouseLeave={handleMouseLeave}>
            <ResponsiveLine
                {...lineProps}
                data={props.period.toLowerCase() === 'daily' && !props.category === 'product' ? processDataForAxis(props.data) : props.data}
                theme={{
                    fontSize: 10,
                    crosshair: {
                        line: {
                            stroke: themeV2.color.base.gray.dark,
                            strokeWidth: 1,
                            strokeOpacity: 0.75,
                            strokeDasharray: ''
                        }
                    },
                }}
                yScale={{
                    type: "linear",
                    stacked: false,
                    min: 0,
                    max: maxY && !Number.isNaN(maxY) ? maxY : 'auto'
                }}
                axisLeft={{
                    orient: 'left',
                    tickSize: 0,
                    tickPadding: 20,
                    tickRotation: 0,
                    legend: null,
                    tickValues: 4,
                    format: value => formatYAxis(value, props.yType)
                }}
                tooltip={handleHover}
                onClick={handleClick}
                enableGridX={false}
                enableGridY={true}
                gridYValues={4}
                colors={d => d.color}
                lineWidth={2}
                enablePoints={props.enablePoints}
                pointSize={8}
                pointColor={{ theme: 'background' }}
                pointBorderWidth={2}
                pointBorderColor={{ from: 'serieColor' }}
                enableArea={false}
                areaOpacity={0.3}
                animate={true}
                motionStiffness={90}
                motionDamping={15}
                useMesh={true}
                layers={layers}
            />
        </div>
    );
});


const CustomLine = (props) => {
    const { currentX } = props;
    return (
        <>
            <svg>
                <path style={{transition: 'all .5s ease'}}
                    d={`M ${currentX} 0 L ${currentX} 240`}
                    stroke={'rgba(6,43,117, 0.5)'}
                    strokeWidth={1}
                    fill="none"
                />
            </svg>
            <g>
                {props.d?.points.filter(point => point.x === currentX).map((point) => (
                    <circle style={{transition: 'all .5s ease'}}
                        r={4}
                        cx={point.x}
                        cy={point.y}
                        fill="#ffffff"
                        stroke={point.serieColor}
                        strokeWidth={2}
                    />
                ))}
            </g>
        </>
    );
};

const DashedSolidLine = ({ series, lineGenerator, xScale, yScale }) => {
    return series.map(({ id, data, color }) => (
        <path
            key={id}
            d={lineGenerator(
                data.map((d) => ({
                    x: xScale(d.data.x),
                    y: yScale(d.data.y)
                }))
            )}
            fill="none"
            stroke={color}
            style={
                id === 'TOTAL'
                    ? {
                        strokeWidth: 2
                    } : {
                        strokeDasharray: "3, 4",
                        strokeWidth: 2
                    }
            }
        />
    ));
};


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default LineChart;

